/**
 * Client for managing custom columns (TweetDeck-style views)
 */

import { getStoredSessionId } from './auth-client';
import type { UserColumn, CreateColumnInput, UpdateColumnInput } from '@/types/column';

const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';

export class ColumnsClient {
  /**
   * Get all custom columns for the current user
   */
  async getColumns(): Promise<UserColumn[]> {
    const sessionId = getStoredSessionId();

    const response = await fetch(`${CF_WORKER_URL}/api/columns`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
      },
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error?.message || 'Failed to get columns');
    }

    return result.data;
  }

  /**
   * Create a new custom column
   */
  async createColumn(input: CreateColumnInput): Promise<UserColumn> {
    const sessionId = getStoredSessionId();

    const response = await fetch(`${CF_WORKER_URL}/api/columns`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
      },
      body: JSON.stringify(input),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error?.message || 'Failed to create column');
    }

    return result.data;
  }

  /**
   * Update an existing column
   */
  async updateColumn(columnId: string, input: UpdateColumnInput): Promise<UserColumn> {
    const sessionId = getStoredSessionId();

    const response = await fetch(`${CF_WORKER_URL}/api/columns/${columnId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
      },
      body: JSON.stringify(input),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error?.message || 'Failed to update column');
    }

    return result.data;
  }

  /**
   * Delete a column
   */
  async deleteColumn(columnId: string): Promise<void> {
    const sessionId = getStoredSessionId();

    const response = await fetch(`${CF_WORKER_URL}/api/columns/${columnId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
      },
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error?.message || 'Failed to delete column');
    }
  }

  /**
   * Reorder columns
   */
  async reorderColumns(columnIds: string[]): Promise<UserColumn[]> {
    const sessionId = getStoredSessionId();

    const response = await fetch(`${CF_WORKER_URL}/api/columns/reorder`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
      },
      body: JSON.stringify({ columnIds }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error?.message || 'Failed to reorder columns');
    }

    return result.data;
  }
}

// Singleton instance
export const columnsClient = new ColumnsClient();
